import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

const AdminDashboardVM = () => {
  // Dashboard state
  const [selectedRole, setSelectedRole] = useState("");
  const [approvers, setApprovers] = useState([]);
  const [gatekeepers, setGatekeepers] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Updated file state management
  const [selectedFile, setSelectedFile] = useState(null); // Single file instead of array
  const [fileName, setFileName] = useState("Choose File");
  
  // Add file input ref for clearing
  const fileInputRef = useRef(null);
  
  // Search state
  const [searchText, setSearchText] = useState("");

  // Countries state
  const [countries, setCountries] = useState([]);

  // Validation
  const [validationErrors, setValidationErrors] = useState({
    full_name: "",
    contact_number: "",
    email: "",
    country_code: "",
  });

  // Feedback messages
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // IconTabs state
  const [activeTab, setActiveTab] = useState("Approver");
  const [newUser, setNewUser] = useState({
    full_name: "",
    contact_number: "",
    email: "",
    country_code: "+91", // Default to India
  });
  const [showNewRow, setShowNewRow] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    userId: null,
    userRole: null,
    userName: ""
  });

  const navigate = useNavigate();
  const { handleToastOpen } = useToast();

  // FIXED: Stable event handlers with empty dependency arrays
  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value);
  }, []); // Empty dependency array - prevents input recreation

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    setValidationErrors(prev => ({
      ...prev,
      [name]: ""
    }));
  }, []); // Empty dependency array - prevents input recreation

  const handleRoleChange = useCallback((e) => {
    setSelectedRole(e.target.value);
  }, []);

  // Updated file upload API call with better debugging
  const addAnyFileToTable = async (selectedApi, file) => {
    try {
      console.log("=== FILE UPLOAD DEBUG ===");
      console.log("API Endpoint:", `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/${selectedApi}`);
      console.log("File object:", file);
      console.log("File name:", file.name);
      console.log("File size:", file.size);
      console.log("File type:", file.type);
      console.log("File lastModified:", file.lastModified);
      
      // Check if file is actually a File object
      console.log("Is File instance:", file instanceof File);
      console.log("File constructor:", file.constructor.name);
      
      const formData = new FormData();
      
      // Try different field names that the backend might expect
      formData.append("file", file);
      formData.append("files", file); // Some backends expect 'files'
      formData.append("upload", file); // Some backends expect 'upload'
      formData.append("csvFile", file); // Some backends expect 'csvFile'
      formData.append("bulkFile", file); // Some backends expect 'bulkFile'
      
      // Log FormData contents
      console.log("=== FORMDATA CONTENTS ===");
      for (let pair of formData.entries()) {
        console.log(`${pair[0]}:`, pair[1]);
        if (pair[1] instanceof File) {
          console.log(`  -> File name: ${pair[1].name}`);
          console.log(`  -> File size: ${pair[1].size}`);
          console.log(`  -> File type: ${pair[1].type}`);
        }
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/${selectedApi}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 30000,
          // Add progress tracking for debugging
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload Progress: ${percentCompleted}%`);
          },
        }
      );
      
      console.log("=== UPLOAD RESPONSE ===");
      console.log("Response status:", response.status);
      console.log("Response headers:", response.headers);
      console.log("Response data:", response.data);
      
      if (response.status === 200 || response.status === 201) {
        if (response.data.status === undefined || 
            response.data.status === 200 || 
            response.data.status === 201) {
          return response.data;
        } else {
          throw new Error(response.data.message || "Upload failed");
        }
      } else {
        throw new Error(`Server responded with status: ${response.status}`);
      }
    } catch (error) {
      console.error("=== UPLOAD ERROR ===");
      console.error("Error object:", error);
      
      if (error.response) {
        console.error("Error response status:", error.response.status);
        console.error("Error response headers:", error.response.headers);
        console.error("Error response data:", error.response.data);
        
        // Check if it's a field name issue
        if (error.response.data && typeof error.response.data === 'string' && 
            error.response.data.includes('file')) {
          console.error("Possible field name issue. Backend might expect different field name.");
        }
        
        throw new Error(error.response.data.message || error.response.data.error || "Server error");
      } else if (error.request) {
        console.error("Request made but no response:", error.request);
        throw new Error("Network error - please check your connection");
      } else {
        console.error("Error setting up request:", error.message);
        throw new Error(error.message || "Unknown error occurred");
      }
    }
  };

  // Alternative version with minimal field names for testing
  const addAnyFileToTableMinimal = async (selectedApi, file) => {
    try {
      console.log("=== MINIMAL UPLOAD ATTEMPT ===");
      console.log("File:", file);
      
      const formData = new FormData();
      formData.append("file", file); // Start with just 'file'
      
      // Log what we're sending
      console.log("FormData entries:");
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/${selectedApi}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 30000,
        }
      );
      
      console.log("Minimal upload response:", response);
      return response.data;
    } catch (error) {
      console.error("Minimal upload error:", error);
      throw error;
    }
  };

  // Updated handleFileUpload with detailed response handling
  const handleFileUpload = async () => {
    console.log("=== STARTING FILE UPLOAD ===");
    
    if (!selectedRole) {
      const errorMsg = "Please select a role first";
      setErrorMessage(errorMsg);
      handleToastOpen("error", errorMsg);
      return;
    }

    if (!selectedFile) {
      console.error("No file selected");
      const errorMsg = "Please select a file to upload";
      setErrorMessage(errorMsg);
      handleToastOpen("error", errorMsg);
      return;
    }

    // Additional file validation
    if (!(selectedFile instanceof File)) {
      console.error("Selected file is not a File object");
      const errorMsg = "Invalid file selected";
      setErrorMessage(errorMsg);
      handleToastOpen("error", errorMsg);
      return;
    }

    if (selectedFile.size === 0) {
      console.error("File is empty");
      const errorMsg = "Cannot upload empty file";
      setErrorMessage(errorMsg);
      handleToastOpen("error", errorMsg);
      return;
    }

    setLoading(true);
    setErrorMessage("");
    
    try {
      console.log("Starting upload for file:", selectedFile.name);
      console.log("API endpoint will be:", `bulkUpload/${selectedRole}`);
      
      // Try the main upload function first
      const uploadResult = await addAnyFileToTable(`bulkUpload/${selectedRole}`, selectedFile);
      
      // Handle detailed upload response
      if (uploadResult && uploadResult.success) {
        const {
          countAll = 0,
          duplicates = 0,
          skipped = 0,
          total = 0,
          emailsSent = 0,
          emailsFailed = 0,
          temporaryPassword = "12345"
        } = uploadResult;
        
        // Create detailed success message
        let successDetails = [];
        if (countAll > 0) successDetails.push(`${countAll} new users added`);
        if (duplicates > 0) successDetails.push(`${duplicates} existing users updated`);
        if (skipped > 0) successDetails.push(`${skipped} records skipped due to validation errors`);
        if (emailsSent > 0) successDetails.push(`${emailsSent} welcome emails sent`);
        if (emailsFailed > 0) successDetails.push(`${emailsFailed} emails failed to send`);
        
        const successMsg = `Upload completed successfully! ${successDetails.join(', ')}.`;
        setSuccessMessage(successMsg);
        
        // Show detailed toast with password info
        const toastMsg = `${countAll} ${selectedRole}s processed. Temporary password: ${temporaryPassword}`;
        handleToastOpen("success", toastMsg);
        
        // Show additional info if there were issues
        if (skipped > 0 || emailsFailed > 0) {
          const warningMsg = `Note: ${skipped} records were skipped due to validation errors (invalid email/phone format or missing fields). ${emailsFailed} welcome emails failed to send.`;
          setTimeout(() => {
            handleToastOpen("warning", warningMsg);
          }, 2000);
        }
        
      } else {
        throw new Error(uploadResult?.message || "Upload failed");
      }
      
      // Clear file selection after successful upload
      setFileName("Choose File");
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Refresh data
      await fetchData();
    } catch (error) {
      console.error("Upload failed, trying alternative approach...");
      
      // If first attempt fails, try with minimal formdata
      try {
        const uploadResult = await addAnyFileToTableMinimal(`bulkUpload/${selectedRole}`, selectedFile);
        
        // Handle successful alternative upload
        if (uploadResult && uploadResult.success) {
          const {
            countAll = 0,
            duplicates = 0,
            skipped = 0,
            emailsSent = 0,
            emailsFailed = 0,
            temporaryPassword = "12345"
          } = uploadResult;
          
          let successDetails = [];
          if (countAll > 0) successDetails.push(`${countAll} new users added`);
          if (duplicates > 0) successDetails.push(`${duplicates} existing users updated`);
          if (skipped > 0) successDetails.push(`${skipped} records skipped`);
          
          const successMsg = `Upload completed! ${successDetails.join(', ')}.`;
          setSuccessMessage(successMsg);
          
          const toastMsg = `${countAll} ${selectedRole}s processed. Temporary password: ${temporaryPassword}`;
          handleToastOpen("success", toastMsg);
          
          if (skipped > 0 || emailsFailed > 0) {
            const warningMsg = `${skipped} records skipped due to validation errors. ${emailsFailed} emails failed.`;
            setTimeout(() => {
              handleToastOpen("warning", warningMsg);
            }, 2000);
          }
        } else {
          throw new Error(uploadResult?.message || "Upload failed");
        }
        
        // Clear file selection after successful upload
        setFileName("Choose File");
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        await fetchData();
      } catch (secondError) {
        // Handle upload errors with specific messages
        let errorMsg = "Failed to upload file";
        
        if (secondError.response) {
          const { status, data } = secondError.response;
          if (status === 400) {
            // Handle validation errors from backend
            if (data.message) {
              if (data.message.includes("missing fields")) {
                errorMsg = "File contains records with missing required fields (full_name, contact_number, email)";
              } else if (data.message.includes("invalid email")) {
                errorMsg = "File contains records with invalid email formats";
              } else if (data.message.includes("invalid contact number")) {
                errorMsg = "File contains records with invalid contact numbers (must be 10 digits)";
              } else if (data.message.includes("no data")) {
                errorMsg = "The uploaded file contains no data rows";
              } else if (data.message.includes("Invalid Excel file")) {
                errorMsg = "Invalid file format. Please upload a valid Excel (.xlsx) or CSV file";
              } else {
                errorMsg = data.message;
              }
            } else {
              errorMsg = "Invalid file format or content";
            }
          } else if (status === 413) {
            errorMsg = "File is too large";
          } else if (status >= 500) {
            errorMsg = "Server error occurred during upload";
          } else {
            errorMsg = data.message || "Upload failed";
          }
        } else if (secondError.request) {
          errorMsg = "Network error during upload. Please check your connection.";
        } else {
          errorMsg = secondError.message || "Unknown upload error";
        }
        
        setErrorMessage(errorMsg);
        handleToastOpen("error", errorMsg);
        console.error("Both upload attempts failed:", secondError);
      }
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Enhanced file change handler with stable reference
  const handleFileChange = useCallback((e) => {
    console.log("=== FILE SELECTION EVENT ===");
    console.log("Event:", e);
    console.log("Files:", e.target.files);
    console.log("Files length:", e.target.files.length);
    
    const file = e.target.files[0];
    
    if (file) {
      console.log("=== SELECTED FILE DETAILS ===");
      console.log("File object:", file);
      console.log("File name:", file.name);
      console.log("File size:", file.size);
      console.log("File type:", file.type);
      console.log("File lastModified:", new Date(file.lastModified));
      console.log("File instanceof File:", file instanceof File);
      
      // Validate file type
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const allowedExtensions = ['csv', 'xlsx', 'xls'];
      
      console.log("File extension:", fileExtension);
      console.log("Allowed extensions:", allowedExtensions);
      console.log("File MIME type:", file.type);
      console.log("Allowed MIME types:", allowedTypes);
      
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        console.error("Invalid file type");
        handleToastOpen("error", "Please select a valid CSV or Excel file");
        setSelectedFile(null);
        setFileName("Choose File");
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        console.error("File too large");
        handleToastOpen("error", "File size must be less than 10MB");
        setSelectedFile(null);
        setFileName("Choose File");
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      if (file.size === 0) {
        console.error("File is empty");
        handleToastOpen("error", "Cannot select empty file");
        setSelectedFile(null);
        setFileName("Choose File");
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      console.log("File validation passed, setting selectedFile");
      setSelectedFile(file);
      setFileName(file.name);
      console.log("File selection complete");
    } else {
      console.log("No file selected");
      setSelectedFile(null);
      setFileName("Choose File");
    }
  }, [handleToastOpen]); // Only include handleToastOpen which should be stable

  // Filtered data calculation with useMemo
  const filteredData = useMemo(() => {
    if (searchText.trim() === "") {
      return null; // Return null to use original data
    }

    const searchLower = searchText.toLowerCase();
    let sourceData = [];

    switch (activeTab) {
      case "Approver":
        sourceData = approvers;
        break;
      case "Gatekeeper":
        sourceData = gatekeepers;
        break;
      case "Visitor":
        sourceData = visitors;
        break;
      default:
        sourceData = [];
    }

    return sourceData.filter((user) =>
      user.full_name?.toLowerCase().includes(searchLower) ||
      user.contact_number?.toLowerCase().includes(searchLower) ||
      user.mobile_number?.toLowerCase().includes(searchLower) ||
      user.phone?.toLowerCase().includes(searchLower) ||
      user.mobile?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  }, [searchText, activeTab, approvers, gatekeepers, visitors]);

  // Get active tab data
  const getActiveTabData = useMemo(() => {
    switch (activeTab) {
      case "Approver":
        return Array.isArray(approvers) ? approvers : [];
      case "Gatekeeper":
        return Array.isArray(gatekeepers) ? gatekeepers : [];
      case "Visitor":
        return Array.isArray(visitors) ? visitors : [];
      default:
        return [];
    }
  }, [activeTab, approvers, gatekeepers, visitors]);

  // Clear alerts
  const closeAlerts = useCallback(() => {
    setSuccessMessage("");
    setErrorMessage("");
  }, []);

  // Check if user is admin
  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "Admin") {
      navigate("/visitor-mgmt/login");
    }
  }, [navigate]);

  // Fetch initial data
  useEffect(() => {
    fetchData();
    fetchCountries();
  }, []);

  // Fetch countries data
  const fetchCountries = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/getCountryCodes`
      );
      
      if (response.data.success && Array.isArray(response.data.data)) {
        setCountries(response.data.data);
      } else {
        console.warn("No countries data received");
        setCountries([]);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountries([]);
    }
  };

  // Update search when switching tabs
  useEffect(() => {
    setSearchText("");
  }, [activeTab]);

  const fetchData = async () => {
    console.log("Fetching data...");
    setLoading(true);
    setErrorMessage("");
    
    // Get admin's email from localStorage (this should contain the admin's email)
    const adminEmail = localStorage.getItem("userEmail") || localStorage.getItem("contactInfo");
    console.log("Admin email from localStorage:", adminEmail);
    
    try {
      // Fetch Approvers with admin email for backend terminal filtering
      try {
        let approverUrl = `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/roles?role=Approver`;
        if (adminEmail) {
          approverUrl += `&admin_email=${encodeURIComponent(adminEmail)}`;
        }
        
        console.log("Fetching approvers from:", approverUrl);
        const approverRes = await axios.get(approverUrl);
        if (approverRes.data && Array.isArray(approverRes.data)) {
          setApprovers(approverRes.data);
        } else if (approverRes.data?.data && Array.isArray(approverRes.data.data)) {
          setApprovers(approverRes.data.data);
        } else {
          setApprovers([]);
        }
      } catch (error) {
        console.error("Error fetching approver data:", error);
        setApprovers([]);
      }

      // Fetch Gatekeepers with admin email for backend terminal filtering
      try {
        let gatekeeperUrl = `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/roles?role=Gatekeeper`;
        if (adminEmail) {
          gatekeeperUrl += `&admin_email=${encodeURIComponent(adminEmail)}`;
        }
        
        console.log("Fetching gatekeepers from:", gatekeeperUrl);
        const gatekeeperRes = await axios.get(gatekeeperUrl);
        if (gatekeeperRes.data && Array.isArray(gatekeeperRes.data)) {
          setGatekeepers(gatekeeperRes.data);
        } else if (gatekeeperRes.data?.data && Array.isArray(gatekeeperRes.data.data)) {
          setGatekeepers(gatekeeperRes.data.data);
        } else {
          setGatekeepers([]);
        }
      } catch (error) {
        console.error("Error fetching gatekeeper data:", error);
        setGatekeepers([]);
      }

      // Fetch Visitors with admin email for backend terminal filtering
      try {
        let visitorUrl = `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/roles?role=Visitor`;
        if (adminEmail) {
          visitorUrl += `&admin_email=${encodeURIComponent(adminEmail)}`;
        }
        
        console.log("Fetching visitors from:", visitorUrl);
        const visitorRes = await axios.get(visitorUrl);
        if (visitorRes.data && Array.isArray(visitorRes.data)) {
          setVisitors(visitorRes.data);
        } else if (visitorRes.data?.data && Array.isArray(visitorRes.data.data)) {
          setVisitors(visitorRes.data.data);
        } else {
          setVisitors([]);
        }
      } catch (error) {
        console.error("Error fetching visitor data:", error);
        setVisitors([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      handleToastOpen("error", "Failed to load data. Please check the console for details.");
      setApprovers([]);
      setGatekeepers([]);
      setVisitors([]);
      setErrorMessage("Failed to load data. Please try refreshing.");
    } finally {
      setLoading(false);
    }
  };

  const handleSampleFileDownload = async () => {
    if (!selectedRole) {
      const errorMsg = "Please select a role first";
      setErrorMessage(errorMsg);
      handleToastOpen("error", errorMsg);
      return;
    }

    try {
      const type = selectedRole.toLowerCase();
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/getSampleCsv?type=${type}`,
        { responseType: "blob" }
      );
      
      const blob = new Blob([response.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `Sample${selectedRole}Upload.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      const successMsg = `Successfully downloaded sample template for ${selectedRole} role`;
      setSuccessMessage(successMsg);
      handleToastOpen("success", "File Downloaded Successfully");
    } catch (error) {
      console.error("Download error:", error);
      
      let errorMsg = "Failed to download file";
      
      if (error.response) {
        const { status, data } = error.response;
        if (status === 404) {
          errorMsg = "Sample template not found for this role";
        } else if (status >= 500) {
          errorMsg = "Server error occurred during download";
        } else {
          errorMsg = data.message || "Download failed";
        }
      } else if (error.request) {
        errorMsg = "Network error during download. Please check your connection.";
      } else {
        errorMsg = error.message || "Unknown download error";
      }
      
      setErrorMessage(errorMsg);
      handleToastOpen("error", errorMsg);
    }
  };

  const handleViewDetails = useCallback((user) => {
    navigate(`/approve-visitor/${user.id}`, { state: { user } });
  }, [navigate]);

  const handleDelete = useCallback((id, role, userName) => {
    setConfirmDialog({
      open: true,
      title: "Confirm Deletion",
      message: `Are you sure you want to delete ${userName || 'this user'}? This action cannot be undone.`,
      userId: id,
      userRole: role,
      userName: userName
    });
  }, []);

  const handleConfirmDelete = async () => {
    const { userId, userRole } = confirmDialog;
    
    setLoading(true);
    setErrorMessage("");
    setConfirmDialog({ ...confirmDialog, open: false });
    
    try {
      await axios.delete(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/deleteUser`,
        {
          data: { id: userId, role: userRole },
        }
      );

      if (userRole === "Visitor") {
        setVisitors(visitors.filter((visitor) => visitor.id !== userId));
        setSuccessMessage("Visitor deleted successfully!");
        handleToastOpen("success", "Visitor deleted successfully!");
      } else if (userRole === "Approver") {
        setApprovers(approvers.filter((approver) => approver.id !== userId));
        setSuccessMessage("Approver deleted successfully!");
        handleToastOpen("success", "Approver deleted successfully!");
      } else if (userRole === "Gatekeeper") {
        setGatekeepers(
          gatekeepers.filter((gatekeeper) => gatekeeper.id !== userId)
        );
        setSuccessMessage("Gatekeeper deleted successfully!");
        handleToastOpen("success", "Gatekeeper deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setErrorMessage(`Failed to delete ${userRole.toLowerCase()}: ${error.message || "Unknown error"}`);
      handleToastOpen("error", "Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const validateUserInput = () => {
    const errors = {
      full_name: "",
      contact_number: "",
      email: "",
      country_code: "",
    };
    let isValid = true;

    if (!newUser.full_name || newUser.full_name.trim() === "") {
      errors.full_name = "Name is required";
      isValid = false;
    } else if (!/^[a-zA-Z\s]+$/.test(newUser.full_name)) {
      errors.full_name = "Name should contain only letters";
      isValid = false;
    }

    if (!newUser.contact_number || newUser.contact_number.trim() === "") {
      errors.contact_number = "Contact number is required";
      isValid = false;
    } else if (!/^\d{10}$/.test(newUser.contact_number)) {
      errors.contact_number = "Must be exactly 10 digits";
      isValid = false;
    }

    if (!newUser.email || newUser.email.trim() === "") {
      errors.email = "Email is required";
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUser.email)) {
      errors.email = "Invalid email format";
      isValid = false;
    }

    if (!newUser.country_code || newUser.country_code.trim() === "") {
      errors.country_code = "Country code is required";
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleAddUser = async (user) => {
    try {
      // Get admin's email from localStorage
      const adminEmail = localStorage.getItem("userEmail") || localStorage.getItem("contactInfo");
      
      // Send data with full mobile number (country code + number) and admin email
      const userData = {
        ...newUser,
        mobile_number: `${newUser.country_code}${newUser.contact_number}`, // Combine for backend
      };
      
      const response = await axios.post(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/addUser`,
        {
          role: activeTab,
          user: userData, // Send combined data
          admin_email: adminEmail // Send admin email for backend terminal lookup
        }
      );
    
      // Handle successful response
      if (response.status === 201 && response.data.success) {
        setSuccessMessage(`${activeTab} added successfully!`);
        handleToastOpen("success", `${activeTab} added successfully!`);
        
        // Add user to appropriate state array
        if (activeTab === "Approver") {
          setApprovers((prevApprovers) => [
            ...prevApprovers,
            response.data.data, // Use 'data' field from backend response
          ]);
        } else if (activeTab === "Gatekeeper") {
          setGatekeepers((prevGatekeepers) => [
            ...prevGatekeepers,
            response.data.data, // Use 'data' field from backend response
          ]);
        } else if (activeTab === "Visitor") {
          setVisitors((prevVisitors) => [...prevVisitors, response.data.data]);
        }
        
        return response.data.data;
      } else {
        // Handle unexpected success response structure
        throw new Error(response.data.message || "Unexpected response format");
      }
      
    } catch (error) {
      console.error("Error adding user:", error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        
        if (status === 409) {
          // Conflict - user already exists
          const errorMsg = data.message || "A user with this email or contact number already exists";
          setErrorMessage(errorMsg);
          handleToastOpen("error", errorMsg);
        } else if (status === 400) {
          // Bad request - validation error
          const errorMsg = data.message || "Invalid user data provided";
          setErrorMessage(errorMsg);
          handleToastOpen("error", errorMsg);
        } else if (status >= 500) {
          // Server error
          const errorMsg = "Server error occurred. Please try again later.";
          setErrorMessage(errorMsg);
          handleToastOpen("error", errorMsg);
        } else {
          // Other HTTP errors
          const errorMsg = data.message || `Failed to add ${activeTab.toLowerCase()}`;
          setErrorMessage(errorMsg);
          handleToastOpen("error", errorMsg);
        }
      } else if (error.request) {
        // Network error - no response received
        const errorMsg = "Network error. Please check your connection and try again.";
        setErrorMessage(errorMsg);
        handleToastOpen("error", errorMsg);
      } else {
        // Other errors (validation, etc.)
        const errorMsg = error.message || `Failed to add ${activeTab.toLowerCase()}`;
        setErrorMessage(errorMsg);
        handleToastOpen("error", errorMsg);
      }
      
      throw error; // Re-throw to be handled by handleSaveUser
    }
  };

  const handleAddRow = useCallback(() => {
    setShowNewRow(true);
    setNewUser({
      full_name: "",
      contact_number: "",
      email: "",
      country_code: "+91", // Default to India
    });
    setValidationErrors({
      full_name: "",
      contact_number: "",
      email: "",
      country_code: "",
    });
  }, []);

  const handleSaveUser = async () => {
    // Clear any previous error messages
    setErrorMessage("");
    
    if (!validateUserInput()) {
      // Validation errors are already set by validateUserInput()
      return;
    }

    setLoading(true);
    try {
      const addedUser = await handleAddUser(newUser);
      if (addedUser) {
        // Success - clear form and hide the new row
        setNewUser({
          full_name: "",
          contact_number: "",
          email: "",
          country_code: "+91", // Reset to default
        });
        setValidationErrors({
          full_name: "",
          contact_number: "",
          email: "",
          country_code: "",
        });
        setShowNewRow(false);
      }
    } catch (error) {
      // Error is already handled in handleAddUser
      console.error("Failed to add user:", error);
      // Don't hide the form row so user can correct the error
    } finally {
      setLoading(false);
    }
  };

  const handleChangeTab = useCallback((tabName) => {
    console.log("Changing tab to:", tabName);
    setActiveTab(tabName);
    setShowNewRow(false);
    setErrorMessage("");
    setSuccessMessage("");
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  }, []);

  const refreshData = useCallback(() => {
    console.log("Manually refreshing data...");
    setSearchText("");
    setSuccessMessage("");
    setErrorMessage("");
    fetchData();
  }, []);

  // Helper function to clear file input
  const clearFileInput = useCallback(() => {
    setSelectedFile(null);
    setFileName("Choose File");
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  return {
    // Dashboard state
    selectedRole,
    approvers,
    gatekeepers,
    visitors,
    loading,

    // Countries
    countries,
    
    // File state and methods
    selectedFile, // Return selectedFile instead of selectedFiles
    fileName,
    fileInputRef, // Return ref for the component to use
    clearFileInput, // Return helper function
    handleFileChange,
    handleRoleChange,
    handleFileUpload,
    handleSampleFileDownload,

    // IconTabs state and methods
    activeTab,
    newUser,
    showNewRow,
    setShowNewRow,
    getActiveTabData,
    handleViewDetails,
    handleDelete,
    handleChangeTab,
    handleInputChange,
    handleAddRow,
    handleSaveUser,
    formatDateTime,

    // Search functionality
    searchText,
    setSearchText,
    handleSearchChange,
    filteredData,

    // Validation
    validationErrors,
    
    // Alert messages
    successMessage,
    errorMessage,
    setSuccessMessage,
    setErrorMessage,
    closeAlerts,

    // Confirmation dialog
    confirmDialog,
    setConfirmDialog,
    handleConfirmDelete,

    // Additional methods
    refreshData,
  };
};

export default AdminDashboardVM;